import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Switch,
  FormControlLabel,
  TextField,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import SyncIcon from '@mui/icons-material/Sync';
import axiosInstance from '../../services/axiosInstance';

// --- Types (mirrors backend) ---

interface DabasSyncSettings {
  enabled: boolean;
  dailyTime: string;
  retentionDays: number;
}

interface DabasSyncRunSuccess {
  assetId: string;
  title: string;
  articleNumber: string;
  gtin: string;
}

interface DabasSyncRunFailure {
  assetId: string;
  title: string;
  articleNumber: string;
  gtin: string;
  error: string;
  marker: 'orange' | 'red';
}

interface DabasSyncRun {
  runId: string;
  trigger: 'manual' | 'scheduled';
  startedAt: string;
  finishedAt: string;
  totalAssets: number;
  successCount: number;
  failedCount: number;
  successes: DabasSyncRunSuccess[];
  failures: DabasSyncRunFailure[];
}

interface DabasSyncDayLog {
  date: string;
  runs: DabasSyncRun[];
}

interface DabasSyncLogSummary {
  date: string;
  runCount: number;
  totalSuccess: number;
  totalFailed: number;
  lastRunAt: string | null;
}

const DabasSyncPage: React.FC = () => {
  // Settings state
  const [settings, setSettings] = useState<DabasSyncSettings>({ enabled: false, dailyTime: '02:00', retentionDays: 30 });
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [settingsMsg, setSettingsMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Manual run state
  const [running, setRunning] = useState(false);
  const [runResult, setRunResult] = useState<{
    totalAssets: number;
    successCount: number;
    failedCount: number;
    successes: DabasSyncRunSuccess[];
    failures: DabasSyncRunFailure[];
  } | null>(null);
  const [runDialogOpen, setRunDialogOpen] = useState(false);

  // Logs state
  const [logSummaries, setLogSummaries] = useState<DabasSyncLogSummary[]>([]);
  const [logsLoading, setLogsLoading] = useState(true);
  const [selectedDayLog, setSelectedDayLog] = useState<DabasSyncDayLog | null>(null);
  const [dayLogLoading, setDayLogLoading] = useState(false);

  // Load settings
  const loadSettings = useCallback(async () => {
    try {
      setSettingsLoading(true);
      const { data } = await axiosInstance.get('/api/dabas-sync/settings');
      if (data.settings) setSettings(data.settings);
    } catch (err: any) {
      console.error('Error loading Dabas sync settings:', err);
    } finally {
      setSettingsLoading(false);
    }
  }, []);

  // Load log summaries
  const loadLogs = useCallback(async () => {
    try {
      setLogsLoading(true);
      const { data } = await axiosInstance.get('/api/dabas-sync/logs');
      if (data.logs) setLogSummaries(data.logs);
    } catch (err: any) {
      console.error('Error loading Dabas sync logs:', err);
    } finally {
      setLogsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSettings();
    loadLogs();
  }, [loadSettings, loadLogs]);

  // Save settings
  const handleSaveSettings = async () => {
    try {
      setSettingsSaving(true);
      setSettingsMsg(null);
      const { data } = await axiosInstance.put('/api/dabas-sync/settings', settings);
      if (data.settings) setSettings(data.settings);
      setSettingsMsg({ type: 'success', text: 'Inställningar sparade' });
    } catch (err: any) {
      setSettingsMsg({ type: 'error', text: err.response?.data?.message || 'Kunde inte spara inställningar' });
    } finally {
      setSettingsSaving(false);
    }
  };

  // Manual run
  const handleManualRun = async () => {
    try {
      setRunning(true);
      setRunResult(null);
      setRunDialogOpen(true);
      const { data } = await axiosInstance.post('/api/dabas-sync/run');
      setRunResult({
        totalAssets: data.totalAssets || 0,
        successCount: data.successCount || 0,
        failedCount: data.failedCount || 0,
        successes: data.successes || [],
        failures: data.failures || [],
      });
      loadLogs(); // Refresh logs after run
    } catch (err: any) {
      setRunResult({
        totalAssets: 0,
        successCount: 0,
        failedCount: 0,
        successes: [],
        failures: [{ assetId: '', title: '', articleNumber: '', gtin: '', error: err.response?.data?.message || err.message || 'Okänt fel', marker: 'red' }],
      });
    } finally {
      setRunning(false);
    }
  };

  // Load day log detail
  const handleViewDayLog = async (dateKey: string) => {
    try {
      setDayLogLoading(true);
      setSelectedDayLog(null);
      const { data } = await axiosInstance.get(`/api/dabas-sync/logs/${dateKey}`);
      if (data.log) setSelectedDayLog(data.log);
    } catch (err: any) {
      console.error('Error loading day log:', err);
    } finally {
      setDayLogLoading(false);
    }
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 3, fontWeight: 600, color: '#072f53' }}>
        Dabas-synkronisering
      </Typography>

      {/* Settings Section */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Automatisk synkronisering</Typography>
        {settingsLoading ? (
          <CircularProgress size={24} />
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={settings.enabled}
                  onChange={(e) => setSettings(prev => ({ ...prev, enabled: e.target.checked }))}
                />
              }
              label="Aktivera automatisk nattlig synkronisering"
            />
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              <TextField
                type="time"
                label="Tid (Stockholm)"
                value={settings.dailyTime}
                onChange={(e) => setSettings(prev => ({ ...prev, dailyTime: e.target.value }))}
                size="small"
                sx={{ width: 160 }}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                type="number"
                label="Loggar behålls (dagar)"
                value={settings.retentionDays}
                onChange={(e) => setSettings(prev => ({ ...prev, retentionDays: Math.max(1, Number(e.target.value) || 30) }))}
                size="small"
                sx={{ width: 180 }}
                inputProps={{ min: 1 }}
              />
              <Button
                variant="contained"
                onClick={handleSaveSettings}
                disabled={settingsSaving}
                sx={{ backgroundColor: '#072f53', '&:hover': { backgroundColor: '#0a3d6b' } }}
              >
                {settingsSaving ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Spara'}
              </Button>
            </Box>
            {settingsMsg && (
              <Alert severity={settingsMsg.type} sx={{ mt: 1 }} onClose={() => setSettingsMsg(null)}>
                {settingsMsg.text}
              </Alert>
            )}
            <Typography variant="body2" color="text.secondary">
              Synkroniseringen körs dagligen kl {settings.dailyTime} (Europe/Stockholm) och synkar alla produkter
              som tillhör mallar med Dabas-integration aktiverad. Arkiverade och slängda produkter exkluderas.
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Manual Run Section */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="h6">Manuell körning</Typography>
          <Button
            variant="outlined"
            startIcon={running ? <CircularProgress size={16} /> : <PlayArrowIcon />}
            onClick={handleManualRun}
            disabled={running}
            sx={{ borderColor: '#072f53', color: '#072f53', '&:hover': { borderColor: '#0a3d6b', backgroundColor: '#e3f2fd' } }}
          >
            {running ? 'Kör...' : 'Kör nu'}
          </Button>
        </Box>
        <Typography variant="body2" color="text.secondary">
          Starta en manuell Dabas-synkronisering. Resultatet loggas i dagsloggen.
        </Typography>
      </Paper>

      {/* Run Result Dialog */}
      <Dialog open={runDialogOpen} onClose={() => { if (!running) setRunDialogOpen(false); }} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SyncIcon />
          Dabas-synkronisering
        </DialogTitle>
        <DialogContent>
          {running && (
            <Box sx={{ py: 2 }}>
              <LinearProgress />
              <Typography variant="body2" sx={{ mt: 1 }}>Synkroniserar produkter med Dabas...</Typography>
            </Box>
          )}
          {runResult && !running && (
            <Box sx={{ py: 1 }}>
              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <Chip label={`Totalt: ${runResult.totalAssets}`} variant="outlined" />
                <Chip label={`Lyckade: ${runResult.successCount}`} color="success" variant="outlined" icon={<CheckCircleIcon />} />
                <Chip label={`Misslyckade: ${runResult.failedCount}`} color="error" variant="outlined" icon={<CancelIcon />} />
              </Box>

              {runResult.failures.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, color: '#d32f2f' }}>Misslyckade produkter:</Typography>
                  <TableContainer sx={{ maxHeight: 300 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>Produkt</TableCell>
                          <TableCell>Artikelnr</TableCell>
                          <TableCell>GTIN</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Fel</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {runResult.failures.map((f, i) => (
                          <TableRow key={i}>
                            <TableCell>{f.title || f.assetId}</TableCell>
                            <TableCell>{f.articleNumber || '-'}</TableCell>
                            <TableCell>{f.gtin || '-'}</TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                label={f.marker === 'orange' ? 'Tidigare synkad' : 'Aldrig synkad'}
                                sx={{
                                  backgroundColor: f.marker === 'orange' ? '#fff3e0' : '#ffebee',
                                  color: f.marker === 'orange' ? '#e65100' : '#c62828',
                                }}
                              />
                            </TableCell>
                            <TableCell sx={{ fontSize: '0.75rem' }}>{f.error}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

              {runResult.successes.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1, color: '#2e7d32' }}>Lyckade produkter:</Typography>
                  <TableContainer sx={{ maxHeight: 300 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>Produkt</TableCell>
                          <TableCell>Artikelnr</TableCell>
                          <TableCell>GTIN</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {runResult.successes.map((s, i) => (
                          <TableRow key={i}>
                            <TableCell>{s.title || s.assetId}</TableCell>
                            <TableCell>{s.articleNumber || '-'}</TableCell>
                            <TableCell>{s.gtin || '-'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRunDialogOpen(false)} disabled={running}>Stäng</Button>
        </DialogActions>
      </Dialog>

      {/* Logs Section */}
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Dagliga loggar</Typography>
          <Button size="small" onClick={loadLogs} disabled={logsLoading}>
            Uppdatera
          </Button>
        </Box>

        {logsLoading ? (
          <CircularProgress size={24} />
        ) : logSummaries.length === 0 ? (
          <Typography variant="body2" color="text.secondary">Inga loggar tillgängliga ännu.</Typography>
        ) : (
          <Box>
            {logSummaries.map((summary) => (
              <Accordion
                key={summary.date}
                onChange={(_, expanded) => {
                  if (expanded) handleViewDayLog(summary.date);
                }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                    <Typography sx={{ fontWeight: 500, minWidth: 100 }}>{summary.date}</Typography>
                    <Chip size="small" label={`${summary.runCount} körning${summary.runCount !== 1 ? 'ar' : ''}`} variant="outlined" />
                    <Chip size="small" label={`${summary.totalSuccess} lyckade`} color="success" variant="outlined" />
                    {summary.totalFailed > 0 && (
                      <Chip size="small" label={`${summary.totalFailed} misslyckade`} color="error" variant="outlined" />
                    )}
                    {summary.lastRunAt && (
                      <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                        Senast: {new Date(summary.lastRunAt).toLocaleString('sv-SE')}
                      </Typography>
                    )}
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  {dayLogLoading && !selectedDayLog ? (
                    <CircularProgress size={20} />
                  ) : selectedDayLog && selectedDayLog.date === summary.date ? (
                    <Box>
                      {selectedDayLog.runs.map((run, idx) => (
                        <Paper key={run.runId} variant="outlined" sx={{ p: 2, mb: idx < selectedDayLog.runs.length - 1 ? 2 : 0 }}>
                          <Box sx={{ display: 'flex', gap: 2, mb: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                            <Chip
                              size="small"
                              label={run.trigger === 'scheduled' ? 'Schemalagd' : 'Manuell'}
                              variant="outlined"
                              color={run.trigger === 'scheduled' ? 'info' : 'default'}
                            />
                            <Typography variant="body2">
                              {new Date(run.startedAt).toLocaleTimeString('sv-SE')} — {new Date(run.finishedAt).toLocaleTimeString('sv-SE')}
                            </Typography>
                            <Chip size="small" label={`${run.successCount}/${run.totalAssets} lyckade`} color="success" variant="outlined" />
                            {run.failedCount > 0 && (
                              <Chip size="small" label={`${run.failedCount} misslyckade`} color="error" variant="outlined" />
                            )}
                          </Box>

                          {run.failures.length > 0 && (
                            <Box sx={{ mb: 1 }}>
                              <Typography variant="subtitle2" sx={{ mb: 0.5, fontSize: '0.8rem', color: '#d32f2f' }}>
                                Misslyckade:
                              </Typography>
                              <TableContainer sx={{ maxHeight: 200 }}>
                                <Table size="small">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell sx={{ py: 0.5 }}>Produkt</TableCell>
                                      <TableCell sx={{ py: 0.5 }}>Artikelnr</TableCell>
                                      <TableCell sx={{ py: 0.5 }}>GTIN</TableCell>
                                      <TableCell sx={{ py: 0.5 }}>Status</TableCell>
                                      <TableCell sx={{ py: 0.5 }}>Fel</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {run.failures.map((f, i) => (
                                      <TableRow key={i}>
                                        <TableCell sx={{ py: 0.5 }}>{f.title || f.assetId}</TableCell>
                                        <TableCell sx={{ py: 0.5 }}>{f.articleNumber || '-'}</TableCell>
                                        <TableCell sx={{ py: 0.5 }}>{f.gtin || '-'}</TableCell>
                                        <TableCell sx={{ py: 0.5 }}>
                                          <CancelIcon sx={{ color: f.marker === 'orange' ? '#ff9800' : '#f44336', fontSize: 16, verticalAlign: 'middle' }} />
                                        </TableCell>
                                        <TableCell sx={{ py: 0.5, fontSize: '0.75rem' }}>{f.error}</TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </TableContainer>
                            </Box>
                          )}

                          {run.successes.length > 0 && (
                            <Box>
                              <Typography variant="subtitle2" sx={{ mb: 0.5, fontSize: '0.8rem', color: '#2e7d32' }}>
                                Lyckade:
                              </Typography>
                              <TableContainer sx={{ maxHeight: 200 }}>
                                <Table size="small">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell sx={{ py: 0.5 }}>Produkt</TableCell>
                                      <TableCell sx={{ py: 0.5 }}>Artikelnr</TableCell>
                                      <TableCell sx={{ py: 0.5 }}>GTIN</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {run.successes.map((s, i) => (
                                      <TableRow key={i}>
                                        <TableCell sx={{ py: 0.5 }}>{s.title || s.assetId}</TableCell>
                                        <TableCell sx={{ py: 0.5 }}>{s.articleNumber || '-'}</TableCell>
                                        <TableCell sx={{ py: 0.5 }}>{s.gtin || '-'}</TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </TableContainer>
                            </Box>
                          )}
                        </Paper>
                      ))}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">Klicka för att ladda logg...</Typography>
                  )}
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default DabasSyncPage;
